import { useState } from 'react';
import { Box, Tabs, Tab } from '@mui/material';
import DnsIcon from '@mui/icons-material/Dns';
import ErrorIcon from '@mui/icons-material/Error';
import SpeedIcon from '@mui/icons-material/Speed';
import StorageIcon from '@mui/icons-material/Storage';
import DataObjectIcon from '@mui/icons-material/DataObject';
import ServiceStatusTab from './ServiceStatusTab';
import ErrorTrackingTab from './ErrorTrackingTab';
import PerformanceTab from './PerformanceTab';
import CacheManagementTab from './CacheManagementTab';
import DatabaseHealthTab from './DatabaseHealthTab';
import { SystemHealthData } from '../../../types/admin';

interface SystemTabsProps {
  data: SystemHealthData;
  loading?: boolean;
  onRefresh: () => void;
  onResolveError: (errorId: string) => Promise<{ success: boolean }>;
  onClearCache: (cacheType?: 'r2' | 'kv' | 'all') => Promise<{ success: boolean }>;
}

type SystemTab = 'services' | 'errors' | 'performance' | 'cache' | 'database';

export default function SystemTabs({ data, loading, onRefresh, onResolveError, onClearCache }: SystemTabsProps) {
  const [activeTab, setActiveTab] = useState<SystemTab>('services');

  const handleTabChange = (_event: React.SyntheticEvent, newValue: SystemTab) => {
    setActiveTab(newValue);
  };

  return (
    <Box>
      {/* Tab Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="System Health Tabs">
          <Tab
            icon={<DnsIcon />}
            iconPosition="start"
            label="Service Status"
            value="services"
            sx={{ minHeight: 64 }}
          />
          <Tab icon={<ErrorIcon />} iconPosition="start" label="Error Tracking" value="errors" sx={{ minHeight: 64 }} />
          <Tab
            icon={<SpeedIcon />}
            iconPosition="start"
            label="Performance"
            value="performance"
            sx={{ minHeight: 64 }}
          />
          <Tab
            icon={<StorageIcon />}
            iconPosition="start"
            label="Cache Management"
            value="cache"
            sx={{ minHeight: 64 }}
          />
          <Tab
            icon={<DataObjectIcon />}
            iconPosition="start"
            label="Database Health"
            value="database"
            sx={{ minHeight: 64 }}
          />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <Box role="tabpanel" hidden={activeTab !== 'services'} aria-labelledby="services-tab">
        {activeTab === 'services' && (
          <ServiceStatusTab
            services={data.services}
            uptimeHistory={data.serviceUptimeHistory}
            responseTimeHistory={data.responseTimeHistory}
            loading={loading}
            lastUpdated={data.lastUpdated}
            onRefresh={onRefresh}
          />
        )}
      </Box>

      <Box role="tabpanel" hidden={activeTab !== 'errors'} aria-labelledby="errors-tab">
        {activeTab === 'errors' && (
          <ErrorTrackingTab
            errors={data.errors}
            errorRateHistory={data.errorRateHistory}
            errorDistribution={data.errorDistribution}
            loading={loading}
            lastUpdated={data.lastUpdated}
            onRefresh={onRefresh}
            onResolveError={onResolveError}
          />
        )}
      </Box>

      <Box role="tabpanel" hidden={activeTab !== 'performance'} aria-labelledby="performance-tab">
        {activeTab === 'performance' && (
          <PerformanceTab
            performance={data.performance}
            responseTimeHistory={data.responseTimeHistory}
            requestRateHistory={data.requestRateHistory}
            queryDurationDistribution={data.queryDurationDistribution}
            loading={loading}
            lastUpdated={data.lastUpdated}
            onRefresh={onRefresh}
          />
        )}
      </Box>

      <Box role="tabpanel" hidden={activeTab !== 'cache'} aria-labelledby="cache-tab">
        {activeTab === 'cache' && (
          <CacheManagementTab
            cacheStats={data.cacheStats}
            hitRateHistory={data.cacheHitRateHistory}
            storageGrowthHistory={data.storageGrowthHistory}
            operationsHistory={data.cacheOperationsHistory}
            topResources={data.topCachedResources}
            loading={loading}
            lastUpdated={data.lastUpdated}
            onRefresh={onRefresh}
            onClearCache={onClearCache}
          />
        )}
      </Box>

      <Box role="tabpanel" hidden={activeTab !== 'database'} aria-labelledby="database-tab">
        {activeTab === 'database' && (
          <DatabaseHealthTab
            databaseHealth={data.databaseHealth}
            growthHistory={data.databaseGrowthHistory}
            slowQueries={data.slowQueries}
            tableSizes={data.tableSizes}
            queryDurationDistribution={data.queryDurationDistribution}
            loading={loading}
            lastUpdated={data.lastUpdated}
            onRefresh={onRefresh}
          />
        )}
      </Box>
    </Box>
  );
}
